import { computed, unref } from 'vue';
import { useOrgStore } from '@/stores/org';
import { useGridPreferencesStore } from '@/stores/gridPreferences';
import { editModeEnabledFor, gridKeyForFiche, opensInEditMode } from '@/utils/openInEditMode';

/**
 * Whether a fiche opens read-only or ready to edit — this user's answer for the
 * list it was opened from, falling back to the organization's.
 *
 * The decision has to wait for the user's preferences to arrive, which is what
 * `opensReadyToEdit()` is for: a fiche reached straight from a link mounts
 * without any list having loaded them, and reading them too early would open the
 * record the organization's way, exactly against the choice the user made. The
 * fetch is the one batched, cached request every grid shares, so on the ordinary
 * path (list → record) it is already resolved.
 *
 * @param {string|Object|Function} entityType The fiche's entity type (ref/getter ok)
 * @param {string|Object|Function} [objectType] An explicit grid key, when the
 *   fiche serves several lists and only the page knows which one (contacts)
 */
export function useRecordOpenMode(entityType, objectType = '') {
    const orgStore = useOrgStore();
    const gridPreferences = useGridPreferencesStore();

    const read = value => (typeof value === 'function' ? value() : unref(value)) || '';

    const gridKey = computed(() => gridKeyForFiche(read(entityType), read(objectType)));

    const editModeEnabled = computed(() =>
        editModeEnabledFor({
            userMode: gridKey.value ? gridPreferences.getOpenMode(gridKey.value) : '',
            orgEnabled: orgStore.objectEditModeEnabled
        })
    );

    /**
     * The same question, answered once this user's per-list choice is known.
     *
     * @param {boolean|Object} editable Whether this user may edit this record now
     * @return {Promise<boolean>}
     */
    async function opensReadyToEdit(editable) {
        await gridPreferences.loadAll();
        return opensInEditMode({ enabled: editModeEnabled.value, editable: !!unref(editable) });
    }

    /**
     * Resolve once the stored preferences are in hand, for a caller that needs
     * `editModeEnabled` itself rather than the yes/no (documents, which pass it
     * through `shouldOpenEditor` with their own extra conditions).
     *
     * @return {Promise<void>}
     */
    function whenLoaded() {
        return gridPreferences.loadAll();
    }

    return { gridKey, editModeEnabled, opensReadyToEdit, whenLoaded };
}
